import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { toAdditionalFieldPairs } from '@/lib/customer-format'

/**
 * Auto-shows any product field not yet promoted to a curated field above
 * (the hybrid model). Labels are prettified and values formatted by type by
 * `toAdditionalFieldPairs`. New fields added product-side appear here on their
 * own — no code change required.
 *
 * - `variant="card"`  (detail page): a full Card; shows an explanatory note
 *   when there are no extra fields yet.
 * - `variant="inline"` (inside a vehicle card): a compact block; renders
 *   nothing when empty, to avoid cluttering every vehicle row.
 */
export function AdditionalDetails({
  fields,
  variant = 'card',
}: {
  fields?: Record<string, unknown> | null
  variant?: 'card' | 'inline'
}) {
  const pairs = toAdditionalFieldPairs(fields)

  if (variant === 'inline') {
    if (pairs.length === 0) return null
    return (
      <div className="mt-3 border-t border-cmd-highest/60 pt-3">
        <p className="mb-2 font-label text-[0.6rem] uppercase tracking-widest text-cmd-outline">
          Additional details
        </p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3">
          {pairs.map((p) => (
            <div key={p.key}>
              <dt className="font-label text-[0.6rem] uppercase tracking-wider text-cmd-outline">
                {p.label}
              </dt>
              <dd className="font-mono text-xs text-white/80">{p.value}</dd>
            </div>
          ))}
        </dl>
      </div>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional details</CardTitle>
      </CardHeader>
      <CardContent>
        {pairs.length === 0 ? (
          <p className="text-sm text-cmd-outline">
            No additional fields yet. New fields added in the product appear here
            automatically, until one is promoted into a curated field above.
          </p>
        ) : (
          <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {pairs.map((p) => (
              <div key={p.key} className="min-w-0">
                <dt className="font-label text-[0.65rem] uppercase tracking-wider text-cmd-outline">
                  {p.label}
                </dt>
                <dd className="break-words font-mono text-sm text-white/90">{p.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  )
}
